import { paymentChannelForMethod } from './paymentMethods';
import { CLINIC_TIMEZONE } from './timezone';
import type { DateRange } from './types';
import type {
  CashierCashSection,
  CashierClinioPaySection,
  CashierMethodBreakdown,
  CashierOutstandingInvoice,
  CashierPaymentRow,
  CashierPeriodGrain,
  CashierPeriodRow,
  CashierReportResult,
  CashierSummary,
} from './cashierTypes';

function asNumber(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function asString(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value);
  return text.length > 0 ? text : null;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item)).filter(Boolean);
}

function asSummary(raw: Record<string, unknown> | undefined): CashierSummary {
  return {
    total_received_cents: asNumber(raw?.total_received_cents),
    refunds_cents: asNumber(raw?.refunds_cents),
    net_received_cents: asNumber(raw?.net_received_cents),
    transaction_count: asNumber(raw?.transaction_count),
    refund_count: asNumber(raw?.refund_count),
    outstanding_cents: asNumber(raw?.outstanding_cents),
    outstanding_count: asNumber(raw?.outstanding_count),
  };
}

function asOutstandingInvoice(raw: Record<string, unknown>): CashierOutstandingInvoice {
  return {
    id: String(raw.id),
    invoice_number: asString(raw.invoice_number),
    patient_id: asString(raw.patient_id),
    patient_name: asString(raw.patient_name),
    issued_at: asString(raw.issued_at),
    due_at: asString(raw.due_at),
    total_cents: asNumber(raw.total_cents),
    paid_cents: asNumber(raw.paid_cents),
    outstanding_cents: asNumber(raw.outstanding_cents),
    status: String(raw.status ?? 'issued'),
    clinic_id: String(raw.clinic_id ?? ''),
    clinic_name: asString(raw.clinic_name),
    related_practitioner_ids: asStringArray(raw.related_practitioner_ids),
  };
}

function asMethod(raw: Record<string, unknown>): CashierMethodBreakdown {
  return {
    method_key: String(raw.method_key ?? 'other'),
    received_cents: asNumber(raw.received_cents),
    refund_cents: asNumber(raw.refund_cents),
    net_cents: asNumber(raw.net_cents),
    transaction_count: asNumber(raw.transaction_count),
    refund_count: asNumber(raw.refund_count),
  };
}

function asPeriodRow(raw: Record<string, unknown>): CashierPeriodRow {
  const bucket = String(raw.bucket ?? '');
  return {
    bucket,
    label: String(raw.label ?? bucket),
    received_cents: asNumber(raw.received_cents),
    refund_cents: asNumber(raw.refund_cents),
    net_cents: asNumber(raw.net_cents),
    transaction_count: asNumber(raw.transaction_count),
    refund_count: asNumber(raw.refund_count),
  };
}

function asTransaction(raw: Record<string, unknown>): CashierPaymentRow {
  const methodKey = String(raw.payment_method_key ?? 'other');
  const kind = raw.kind === 'refund' ? 'refund' : 'payment';
  return {
    id: String(raw.id),
    payment_date: String(raw.payment_date),
    amount_cents: asNumber(raw.amount_cents),
    currency: String(raw.currency ?? 'DKK'),
    payment_method_raw: asString(raw.payment_method_raw),
    payment_method_key: methodKey,
    channel: raw.channel === 'clinio_pay' ? 'clinio_pay' : paymentChannelForMethod(methodKey),
    payment_reference: asString(raw.payment_reference),
    notes: asString(raw.notes),
    invoice_id: asString(raw.invoice_id),
    invoice_number: asString(raw.invoice_number),
    patient_id: asString(raw.patient_id),
    patient_name: asString(raw.patient_name),
    practitioner_id: asString(raw.practitioner_id),
    practitioner_name: asString(raw.practitioner_name),
    related_practitioner_ids: asStringArray(raw.related_practitioner_ids),
    clinic_id: String(raw.clinic_id),
    clinic_name: asString(raw.clinic_name),
    kind,
    status: kind === 'refund' ? 'refunded' : 'received',
  };
}

function asCash(raw: Record<string, unknown> | undefined): CashierCashSection {
  return {
    received_cents: asNumber(raw?.received_cents),
    refund_cents: asNumber(raw?.refund_cents),
    net_cents: asNumber(raw?.net_cents),
    transaction_count: asNumber(raw?.transaction_count),
    refund_count: asNumber(raw?.refund_count),
  };
}

function asClinioPay(raw: Record<string, unknown> | undefined): CashierClinioPaySection {
  const fees = raw?.fees_cents;
  const payout = raw?.payout_received_cents;
  const expected = raw?.expected_payout_cents;
  return {
    received_cents: asNumber(raw?.received_cents),
    refund_cents: asNumber(raw?.refund_cents),
    net_cents: asNumber(raw?.net_cents),
    transaction_count: asNumber(raw?.transaction_count),
    refund_count: asNumber(raw?.refund_count),
    fees_cents: fees == null ? null : asNumber(fees),
    payout_received_cents: payout == null ? null : asNumber(payout),
    expected_payout_cents: expected == null ? null : asNumber(expected),
  };
}

export function parseCashierReportPayload(
  payload: Record<string, unknown>,
  period: DateRange,
): CashierReportResult {
  const transactions = Array.isArray(payload.transactions)
    ? payload.transactions.map((row) => asTransaction(row as Record<string, unknown>))
    : [];
  const methods = Array.isArray(payload.methods)
    ? payload.methods.map((row) => asMethod(row as Record<string, unknown>))
    : [];
  const refunds = Array.isArray(payload.refunds)
    ? payload.refunds.map((row) => asMethod(row as Record<string, unknown>))
    : methods.filter((item) => item.refund_cents > 0);
  const grain = (payload.grain as CashierPeriodGrain) || 'day';
  const outstandingInvoices = Array.isArray(payload.outstanding_invoices)
    ? payload.outstanding_invoices.map((row) => asOutstandingInvoice(row as Record<string, unknown>))
    : [];
  const summary = asSummary(payload.summary as Record<string, unknown> | undefined);
  if (summary.outstanding_count === 0 && outstandingInvoices.length > 0) {
    summary.outstanding_count = outstandingInvoices.length;
    summary.outstanding_cents = outstandingInvoices.reduce((sum, row) => sum + row.outstanding_cents, 0);
  }

  return {
    titleKey: 'reports.types.cashier.label',
    period,
    grain,
    timezone: String(payload.timezone ?? CLINIC_TIMEZONE),
    currency: String(payload.currency ?? 'DKK'),
    clinic_name: asString(payload.clinic_name),
    summary,
    methods,
    refunds,
    cash: asCash(payload.cash as Record<string, unknown> | undefined),
    clinioPay: asClinioPay(payload.clinio_pay as Record<string, unknown> | undefined),
    periodRows: Array.isArray(payload.period_rows)
      ? payload.period_rows.map((row) => asPeriodRow(row as Record<string, unknown>))
      : [],
    transactions,
    transactionsTruncated: Boolean(payload.transactions_truncated),
    transactionTotalCount: asNumber(payload.transaction_total_count),
    outstandingInvoices,
    availableMethods: asStringArray(payload.available_methods),
    empty:
      transactions.length === 0 &&
      outstandingInvoices.length === 0 &&
      summary.transaction_count === 0 &&
      summary.refund_count === 0,
  };
}
